// frontend/shared-ui/src/components/PermissionTree.jsx
import React, { useMemo, useState } from "react";
import Checkbox from "./Checkbox";

/**
 * catalog: [{ group, icon, permissions: [{ key, label, description }] }, ...]
 * selected: string[]
 * onChange: (newSelected: string[]) => void
 */
export default function PermissionTree({
  catalog = [],
  selected = [],
  onChange,
}) {
  const [collapsed, setCollapsed] = useState({});
  const [query, setQuery] = useState("");

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const hasWildcard = selectedSet.has("*");

  const togglePermission = (key) => {
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange?.([...next]);
  };

  const toggleGroup = (group, on) => {
    const next = new Set(selectedSet);
    for (const p of group.permissions) {
      if (on) next.add(p.key);
      else next.delete(p.key);
    }
    onChange?.([...next]);
  };

  const groupStatus = (group) => {
    const keys = group.permissions.map((p) => p.key);
    const hits = keys.filter((k) => selectedSet.has(k)).length;
    if (hits === 0) return "none";
    if (hits === keys.length) return "all";
    return "some";
  };

  const filterGroups = () => {
    if (!query) return catalog;
    const q = query.toLowerCase();
    return catalog
      .map((g) => ({
        ...g,
        permissions: g.permissions.filter(
          (p) =>
            p.label.toLowerCase().includes(q) ||
            p.key.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.permissions.length > 0);
  };

  const groups = filterGroups();

  return (
    <div className="fh-perm-tree">
      {/* Toolbar */}
      <div className="fh-perm-tree__toolbar">
        <input
          className="fh-input"
          placeholder="Search permissions..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="fh-perm-tree__toolbar-actions">
          <button
            type="button"
            className="fh-btn fh-btn--ghost fh-btn--sm"
            onClick={() =>
              onChange?.(
                catalog.flatMap((g) => g.permissions.map((p) => p.key)),
              )
            }
          >
            Select all
          </button>
          <button
            type="button"
            className="fh-btn fh-btn--ghost fh-btn--sm"
            onClick={() => onChange?.([])}
          >
            Clear
          </button>
        </div>
      </div>

      {hasWildcard && (
        <div className="fh-perm-tree__wildcard">
          <strong>★ Wildcard enabled</strong> — this role has <code>*</code>{" "}
          (all permissions). Unchecking individual items below won't matter
          while <code>*</code> is set.
        </div>
      )}

      {groups.map((group) => {
        const status = groupStatus(group);
        const isCollapsed = collapsed[group.id] ?? true;

        return (
          <div key={group.group} className="fh-perm-tree__group">
            <div className="fh-perm-tree__group-head">
              <Checkbox
                checked={status === "all"}
                indeterminate={status === "some"}
                onChange={(checked) => toggleGroup(group, checked)}
                ariaLabel={`Toggle all in ${group.group}`}
              />
              <button
                type="button"
                className="fh-perm-tree__group-toggle"
                onClick={() =>
                  setCollapsed((c) => ({ ...c, [group.group]: !isCollapsed }))
                }
              >
                <span className="fh-perm-tree__group-title">{group.group}</span>
                <span className="fh-perm-tree__group-count">
                  {
                    group.permissions.filter((p) => selectedSet.has(p.key))
                      .length
                  }{" "}
                  / {group.permissions.length}
                </span>
                <span
                  className={`fh-perm-tree__chev ${isCollapsed ? "" : "is-open"}`}
                >
                  ▾
                </span>
              </button>
            </div>

            {!isCollapsed && (
              <div className="fh-perm-tree__group-body">
                {group.permissions.map((p) => (
                  <label key={p.key} className="fh-perm-tree__item">
                    <Checkbox
                      checked={selectedSet.has(p.key)}
                      onChange={() => togglePermission(p.key)}
                      ariaLabel={p.label}
                    />
                    <div className="fh-perm-tree__item-meta">
                      <div className="fh-perm-tree__item-label">{p.label}</div>
                      <code className="fh-perm-tree__item-key">{p.key}</code>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {groups.length === 0 && (
        <div className="fh-perm-tree__empty">
          No permissions match your search.
        </div>
      )}
    </div>
  );
}
